import { Form, type FormInstance } from "antd";
import { useEffect, useRef, useState } from "react";
import type { NamePath } from "antd/es/form/interface";

/**
 * Provide a function that inserts text into an input field, then focuses the field and positions the cursor after the
 * inserted text. Also, provide a boolean indicating busy-ness, that is, indicating whether or not text-insertion
 * operations are currently in progress.
 * @param inputId The ID of the input element to manipulate.
 * @param current The current value of the input element.
 * @param setter Callback to change the current value.
 * @returns Function used to insert text at the current edit point, and flag indicating if we're waiting for insertion
 * to finish.
 */
export function useTextInserter(inputId: string, current: unknown, setter: (value: string) => Promise<void>) {
    const timersRef = useCleanupTimersRef();
    const [busyCount, setBusyCount] = useState(0);

    const inserter = async (toInsert: string) => {
        const el = document.getElementById(inputId);
        if (el instanceof HTMLTextAreaElement) {
            const selectionStart = el.selectionStart;

            const [pre, post] = slice(current, selectionStart);
            toInsert = addSpacing(pre, toInsert, post);
            const combined = pre + toInsert + post;

            // Don't allow insertion if we've already reached the maximum length
            if (el.maxLength > 0 && combined.length >= el.maxLength) {
                return;
            }

            await setter(combined);

            const timer = moveSelection(el, selectionStart, toInsert, setBusyCount);
            timersRef.current.push(timer);
        }
    };

    return [inserter, busyCount !== 0] as const;
}

/**
 * Slice a form value into two pieces.
 * @param current The form value to slice apart.
 * @param selectionStart The character to slice the value at. This character will be in the second part.
 * @returns The two parts of the value.
 */
function slice(current: unknown, selectionStart: number) {
    // Forms should only ever have strings and *maybe* numbers (but probably not really in AntD's form system).
    let currentString: string;
    if (typeof current === "string") {
        currentString = current;
    } else if (typeof current === "number") {
        currentString = current.toString();
    } else if (typeof current === "undefined" || current === null) {
        currentString = "";
    } else {
        console.warn("Unexpected form value", typeof current, current);
        currentString = "";
    }

    return [currentString.slice(0, selectionStart), currentString.slice(selectionStart)] as const;
}

/**
 * Add whitespace around the string to insert if necessary.
 * @param pre The prefix we will be concatenating.
 * @param toInsert The string we will be inserting.
 * @param post The postfix we will be concatenating.
 * @returns The string to insert, with spaces added if necessary.
 */
function addSpacing(pre: string, toInsert: string, post: string) {
    if (/^.*\S$/.test(pre)) toInsert = " " + toInsert;
    if (/^\S.*$/.test(post)) toInsert = toInsert + " ";
    return toInsert;
}

/**
 * Move the selection point of the text area after inserting a string.
 * @param el The text area element to manipulate.
 * @param selectionStart The prior selection point the cursor was at.
 * @param toInsert The string that is being inserted.
 * @param setBusyCount Callback to track pending operations.
 * @returns Timer representing the pending operation.
 */
function moveSelection(
    el: HTMLTextAreaElement,
    selectionStart: number,
    toInsert: string,
    setBusyCount: React.Dispatch<React.SetStateAction<number>>,
) {
    const timer = setTimeout(() => {
        el.focus();
        el.selectionEnd = el.selectionStart = selectionStart + toInsert.length;
        setBusyCount((prev) => prev - 1);
    }, 100);
    setBusyCount((prev) => prev + 1);
    return timer;
}

/** Create a ref for storing timers that will automatically cancel timers when unmounted. */
function useCleanupTimersRef() {
    const timersRef = useRef(Array.of<NodeJS.Timeout>());

    // effect is used entirely for cleanup purposes
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            for (const timer of timers) {
                clearTimeout(timer);
            }
        };
    }, []);

    return timersRef;
}

/**
 * Provides a function that inserts text into an input field on an AntD form (with AntD-managed state).
 * Also, provide a boolean indicating busy state, that is, whether or not text-insertion operations are in progress.
 */
export function useFormTextInserter<TForm>(
    formInstance: FormInstance<TForm>,
    formName: string,
    inputName: NamePath<TForm>,
) {
    // Note: AntD FormInstance has a method to theoretically obtain the underlying input element, but it hands back a
    // wrapper object with random properties and there are no type hints, so locating by ID is safer
    const inputId = formName + "_" + inputName;
    const current = Form.useWatch(inputName, formInstance) as unknown;
    const setter = async (nu: string) => {
        formInstance.setFieldValue(inputName, nu);
        try {
            await formInstance.validateFields();
        } catch {
            // we don't want to take action now, we just want validation to occur
        }
    };

    return useTextInserter(inputId, current, setter);
}
